<template>
  <v-container class="account-container">
    <v-row justify="center">
      <v-col cols="12" md="10" lg="8">
        <v-card class="pa-4 elevation-2">
          <v-card-title class="headline mb-2">
            {{ t("message.account.title") }}
          </v-card-title>
          <v-card-text>
            <p class="account-subtitle">
              {{ t("message.account.subtitle") }}
            </p>

            <div v-if="!state.features.premiumEnabled" class="account-card">
              <p>{{ t("message.account.unavailable") }}</p>
            </div>

            <template v-else>
              <div v-if="!state.user" class="account-card">
                <p>{{ t("message.account.loginPrompt") }}</p>
                <p v-if="isDevStudyPlanMockEnabled()" class="account-subtle-note">
                  {{ t("message.account.devMockHint") }}
                </p>
                <div v-if="canUseDevAuthControls" class="dev-auth-actions">
                  <v-btn class="mr-2" outlined @click="signInDevMock(false)">
                    {{ t("message.account.devSignInFree") }}
                  </v-btn>
                  <v-btn outlined @click="signInDevMock(true)">
                    {{ t("message.account.devSignInPremium") }}
                  </v-btn>
                </div>
                <GoogleSignInButton
                  @success="handleGoogleSuccess"
                  @error="handleGoogleError"
                />
              </div>

              <div v-else class="account-card">
                <p>
                  <strong>{{ state.user.name || state.user.email }}</strong>
                  ({{ state.user.email }})
                </p>
                <p>
                  {{ t("message.premium.planStatus") }}:
                  <strong>{{ entitlementLabel }}</strong>
                </p>
                <div class="button-row">
                  <v-btn class="mr-2" color="secondary" @click="refreshMe">
                    {{ t("message.premium.refreshStatus") }}
                  </v-btn>
                  <v-btn color="error" outlined @click="handleLogout">
                    {{ t("message.logout") }}
                  </v-btn>
                </div>
                <div v-if="isDevAuthMockUser()" class="dev-auth-actions">
                  <span class="dev-auth-chip">{{ t("message.account.devMockSession") }}</span>
                  <v-btn class="mr-2" outlined @click="toggleDevMockPaid">
                    {{
                      state.entitlement?.is_paid
                        ? t("message.account.devSwitchToFree")
                        : t("message.account.devSwitchToPremium")
                    }}
                  </v-btn>
                </div>
                <div class="account-tools">
                  <p class="account-tools-title">
                    {{ t("message.premium.accountToolsTitle") }}
                  </p>
                  <div v-if="state.authToken" class="button-row">
                    <v-btn class="mr-2" outlined @click="downloadMyData">
                      {{ t("message.premium.exportData") }}
                    </v-btn>
                    <v-btn color="error" outlined @click="deleteMyAccount">
                      {{ t("message.premium.deleteAccount") }}
                    </v-btn>
                  </div>
                  <p v-else class="account-subtle-note">
                    {{ t("message.account.devMockNoServer") }}
                  </p>
                </div>
              </div>

              <div v-if="showStudyPlanSection" class="study-plan-card">
                <h3 class="section-title">{{ t("message.premium.studyPlanTitle") }}</h3>
                <p class="study-plan-subtitle">
                  {{ t("message.premium.studyPlanSubtitle") }}
                </p>
                <p v-if="!state.user && isDevStudyPlanMockEnabled()" class="account-subtle-note">
                  {{ t("message.account.devMockHint") }}
                </p>
                <SearchBar
                  :items="allCourses"
                  dataType="courses"
                  :showAddButton="true"
                  @search="addStudyPlanCourseFromSearch"
                  @add="addStudyPlanCourseFromSearch"
                />
                <p class="study-plan-count">
                  {{ t("message.premium.studyPlanCount", { count: studyPlanCourses.length }) }}
                </p>
                <p v-if="isLoadingStudyPlan" class="study-plan-empty">
                  {{ t("message.premium.studyPlanLoading") }}
                </p>
                <p v-else-if="!studyPlanCourses.length" class="study-plan-empty">
                  {{ t("message.premium.studyPlanEmpty") }}
                </p>
                <ul v-else class="study-plan-list">
                  <li
                    v-for="course in studyPlanCourses"
                    :key="course.course_code"
                    class="study-plan-item"
                  >
                    <div class="study-plan-item-text">
                      <strong>{{ course.course_code }}</strong>
                      <span v-if="course.course_name"> - {{ course.course_name }}</span>
                    </div>
                    <button
                      class="study-plan-remove-btn"
                      :disabled="studyPlanBusy"
                      @click="removeStudyPlanCourse(course.course_code)"
                    >
                      {{ t("message.dashboard.studyPlanRemoveLabel") }}
                    </button>
                  </li>
                </ul>
              </div>
            </template>

            <p v-if="statusMessage" class="status-message">{{ statusMessage }}</p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { inject } from "vue";
import ApiService from "@/ApiService";
import SearchBar from "@/components/SearchBar.vue";
import GoogleSignInButton from "@/components/GoogleSignInButton.vue";

const IS_PRODUCTION_BUILD = process.env.NODE_ENV === "production";
const envFlag = (value, defaultValue = false) => {
  if (value == null || value === "") return defaultValue;
  return ["1", "true", "yes", "on"].includes(String(value).toLowerCase());
};
const DEV_STUDY_PLAN_MOCK_ENABLED = envFlag(
  process.env.VUE_APP_DEV_MOCK_STUDY_PLAN,
  !IS_PRODUCTION_BUILD
);
const DEV_STUDY_PLAN_STORAGE_KEY = "onlygrades_dev_study_plan_codes";

export default {
  name: "AccountPage",
  components: {
    SearchBar,
    GoogleSignInButton,
  },
  setup() {
    const state = inject("state");

    const t = (key, params = {}) => {
      const keys = key.split(".");
      let value = state.translations[state.locale];
      keys.forEach((k) => {
        value = value?.[k];
      });
      if (typeof value === "string") {
        return value.replace(/\{\{(\w+)\}\}/g, (_, paramKey) => {
          const paramValue = params[paramKey];
          return paramValue == null ? `{{${paramKey}}}` : String(paramValue);
        });
      }
      return value ?? key;
    };

    return { state, t };
  },
  data() {
    return {
      allCourses: [],
      studyPlanCourses: [],
      isLoadingStudyPlan: false,
      studyPlanBusy: false,
      statusMessage: "",
    };
  },
  computed: {
    entitlementLabel() {
      const entitlement = this.state.entitlement || {};
      if (!entitlement.status) return "free";
      return entitlement.status;
    },
    canUseDevAuthControls() {
      return Boolean(
        !IS_PRODUCTION_BUILD && this.state.authMockConfig?.controlsEnabled
      );
    },
    showStudyPlanSection() {
      return Boolean(this.state.user || this.isDevStudyPlanMockEnabled());
    },
  },
  async created() {
    if (!this.state.features.premiumEnabled) {
      return;
    }
    if (this.state.authToken) {
      await this.refreshMe();
    }
    if (this.state.authToken || this.isDevStudyPlanMockEnabled()) {
      await this.fetchCourseCatalog();
      await this.loadStudyPlanCourses();
    }
  },
  methods: {
    isDevStudyPlanMockEnabled() {
      return !IS_PRODUCTION_BUILD && DEV_STUDY_PLAN_MOCK_ENABLED;
    },
    isDevAuthMockUser() {
      return Boolean(this.state.user?.is_mock_user);
    },
    buildDevMockUser() {
      const email =
        String(this.state.authMockConfig?.defaultEmail || "dev@onlygrades.local")
          .trim()
          .toLowerCase() || "dev@onlygrades.local";
      const name =
        String(this.state.authMockConfig?.defaultName || "Dev User").trim() || "Dev User";
      return {
        email,
        name,
        picture_url: null,
        is_mock_user: true,
      };
    },
    buildDevMockEntitlement(isPaid) {
      return isPaid
        ? { plan: "premium", status: "paid", is_paid: true, is_mock: true }
        : { plan: "free", status: "free", is_paid: false, is_mock: true };
    },
    async signInDevMock(isPaid = false) {
      if (!this.canUseDevAuthControls) return;
      this.persistAuth({
        token: "",
        user: this.buildDevMockUser(),
        entitlement: this.buildDevMockEntitlement(isPaid),
      });
      this.statusMessage = this.t("message.account.devSignedIn");
      await this.fetchCourseCatalog();
      await this.loadStudyPlanCourses();
    },
    async toggleDevMockPaid() {
      if (!this.isDevAuthMockUser()) return;
      const nextPaid = !this.state.entitlement?.is_paid;
      await this.signInDevMock(nextPaid);
    },
    normalizeCourseCode(value) {
      return String(value || "").trim().toUpperCase();
    },
    readDevStudyPlanCodes() {
      if (!this.isDevStudyPlanMockEnabled()) return [];
      try {
        const storedValue = localStorage.getItem(DEV_STUDY_PLAN_STORAGE_KEY);
        if (!storedValue) return [];
        const parsedValue = JSON.parse(storedValue);
        if (!Array.isArray(parsedValue)) return [];
        return [
          ...new Set(
            parsedValue.map((code) => this.normalizeCourseCode(code)).filter(Boolean)
          ),
        ];
      } catch (error) {
        console.warn("Could not read dev study plan fallback", error);
        return [];
      }
    },
    persistDevStudyPlanCodes(courseCodes) {
      if (!this.isDevStudyPlanMockEnabled()) return;
      const normalizedCodes = [
        ...new Set(courseCodes.map((code) => this.normalizeCourseCode(code)).filter(Boolean)),
      ];
      try {
        localStorage.setItem(DEV_STUDY_PLAN_STORAGE_KEY, JSON.stringify(normalizedCodes));
      } catch (error) {
        console.warn("Could not persist dev study plan fallback", error);
      }
    },
    buildStudyPlanCoursesFromCodes(courseCodes) {
      return courseCodes
        .map((courseCode) => this.normalizeStudyPlanCourse({ course_code: courseCode }))
        .filter(Boolean)
        .sort((left, right) => left.course_code.localeCompare(right.course_code));
    },
    normalizeStudyPlanCourse(courseLike) {
      const normalizedCode = this.normalizeCourseCode(courseLike?.course_code);
      if (!normalizedCode) return null;
      return {
        course_code: normalizedCode,
        course_name: String(
          courseLike?.course_name || this.resolveCourseName(normalizedCode) || ""
        ).trim(),
      };
    },
    resolveCourseName(courseCode) {
      const normalizedCode = this.normalizeCourseCode(courseCode);
      if (!normalizedCode) return "";
      const matchedCourse = this.allCourses.find(
        (course) =>
          this.normalizeCourseCode(course?.course_code) === normalizedCode
      );
      return matchedCourse?.course_name || "";
    },
    parseStudyPlanResponse(response) {
      const responseCourses = Array.isArray(response?.data?.courses)
        ? response.data.courses
        : [];
      const parsedCourses = responseCourses
        .map((course) => this.normalizeStudyPlanCourse(course))
        .filter(Boolean);
      if (parsedCourses.length) {
        return parsedCourses.sort((left, right) =>
          left.course_code.localeCompare(right.course_code)
        );
      }
      const responseCodes = Array.isArray(response?.data?.course_codes)
        ? response.data.course_codes
        : [];
      return responseCodes
        .map((courseCode) =>
          this.normalizeStudyPlanCourse({ course_code: courseCode })
        )
        .filter(Boolean)
        .sort((left, right) => left.course_code.localeCompare(right.course_code));
    },
    async fetchCourseCatalog() {
      try {
        const response = await ApiService.getCourses();
        const responseCourses = Array.isArray(response?.data?.courses)
          ? response.data.courses
          : null;
        if (responseCourses) {
          this.allCourses = responseCourses
            .map((course) => ({
              course_code: this.normalizeCourseCode(course.course_code),
              course_name: String(course.course_name || "").trim(),
            }))
            .filter((course) => Boolean(course.course_code));
          return;
        }
        const courseCodes = Array.isArray(response?.data?.course_codes)
          ? response.data.course_codes
          : [];
        const courseNames = Array.isArray(response?.data?.course_names)
          ? response.data.course_names
          : [];
        this.allCourses = courseCodes
          .map((courseCode, index) => ({
            course_code: this.normalizeCourseCode(courseCode),
            course_name: String(courseNames[index] || "").trim(),
          }))
          .filter((course) => Boolean(course.course_code));
      } catch (error) {
        console.error("Failed loading courses catalog", error);
      }
    },
    async loadStudyPlanCourses() {
      if (!this.state.authToken) {
        if (this.isDevStudyPlanMockEnabled()) {
          this.studyPlanCourses = this.buildStudyPlanCoursesFromCodes(
            this.readDevStudyPlanCodes()
          );
          return;
        }
        this.studyPlanCourses = [];
        return;
      }
      this.isLoadingStudyPlan = true;
      try {
        const response = await ApiService.getStudyPlanCourses();
        this.studyPlanCourses = this.parseStudyPlanResponse(response);
      } catch (error) {
        console.error("Failed loading study plan courses", error);
        this.studyPlanCourses = [];
        this.statusMessage = this.t("message.premium.studyPlanLoadFailed");
      } finally {
        this.isLoadingStudyPlan = false;
      }
    },
    async addStudyPlanCourseFromSearch(courseLike) {
      if (this.studyPlanBusy) return;
      const requestedCode =
        typeof courseLike === "string" ? courseLike : courseLike?.course_code;
      const normalizedCode = this.normalizeCourseCode(requestedCode);
      if (!normalizedCode) return;
      if (!this.state.authToken) {
        if (!this.isDevStudyPlanMockEnabled()) return;
        const existingCodes = this.readDevStudyPlanCodes();
        if (existingCodes.includes(normalizedCode)) {
          this.statusMessage = this.t("message.dashboard.studyPlanAlreadySaved", {
            code: normalizedCode,
          });
          return;
        }
        const nextCodes = [...existingCodes, normalizedCode];
        this.persistDevStudyPlanCodes(nextCodes);
        this.studyPlanCourses = this.buildStudyPlanCoursesFromCodes(nextCodes);
        this.statusMessage = this.t("message.dashboard.studyPlanSavedDev", {
          code: normalizedCode,
        });
        return;
      }
      if (
        this.studyPlanCourses.some(
          (course) =>
            this.normalizeCourseCode(course.course_code) === normalizedCode
        )
      ) {
        this.statusMessage = this.t("message.dashboard.studyPlanAlreadySaved", {
          code: normalizedCode,
        });
        return;
      }

      this.studyPlanBusy = true;
      try {
        const response = await ApiService.addStudyPlanCourse(normalizedCode);
        this.studyPlanCourses = this.parseStudyPlanResponse(response);
        const savedCode = this.normalizeCourseCode(
          response?.data?.course?.course_code || normalizedCode
        );
        this.statusMessage = this.t("message.dashboard.studyPlanSaved", {
          code: savedCode,
        });
      } catch (error) {
        console.error("Failed adding study plan course", error);
        this.statusMessage = this.t("message.dashboard.studyPlanFailed");
      } finally {
        this.studyPlanBusy = false;
      }
    },
    async removeStudyPlanCourse(courseCode) {
      if (this.studyPlanBusy) return;
      const normalizedCode = this.normalizeCourseCode(courseCode);
      if (!normalizedCode) return;
      if (!this.state.authToken) {
        if (!this.isDevStudyPlanMockEnabled()) return;
        const nextCodes = this.readDevStudyPlanCodes().filter(
          (code) => code !== normalizedCode
        );
        this.persistDevStudyPlanCodes(nextCodes);
        this.studyPlanCourses = this.buildStudyPlanCoursesFromCodes(nextCodes);
        this.statusMessage = this.t("message.dashboard.studyPlanRemovedDev", {
          code: normalizedCode,
        });
        return;
      }

      this.studyPlanBusy = true;
      try {
        const response = await ApiService.removeStudyPlanCourse(normalizedCode);
        this.studyPlanCourses = this.parseStudyPlanResponse(response);
        const removedCode = this.normalizeCourseCode(
          response?.data?.course?.course_code || normalizedCode
        );
        this.statusMessage = this.t("message.dashboard.studyPlanRemoved", {
          code: removedCode,
        });
      } catch (error) {
        console.error("Failed removing study plan course", error);
        this.statusMessage = this.t("message.dashboard.studyPlanFailed");
      } finally {
        this.studyPlanBusy = false;
      }
    },
    persistAuth({ token, user, entitlement }) {
      this.state.authToken = token || "";
      this.state.user = user || null;
      this.state.entitlement =
        entitlement || { plan: "free", status: "free", is_paid: false };
      try {
        if (token) {
          localStorage.setItem(this.state.authStorageKeys.token, token);
        } else {
          localStorage.removeItem(this.state.authStorageKeys.token);
        }
        if (user) {
          localStorage.setItem(this.state.authStorageKeys.user, JSON.stringify(user));
        } else {
          localStorage.removeItem(this.state.authStorageKeys.user);
        }
        localStorage.setItem(
          this.state.authStorageKeys.entitlement,
          JSON.stringify(this.state.entitlement)
        );
      } catch (error) {
        console.warn("Failed to persist auth state", error);
      }
    },
    clearAuth() {
      this.persistAuth({ token: "", user: null, entitlement: null });
      this.state.entitlement = { plan: "free", status: "free", is_paid: false };
    },
    async refreshMe() {
      if (!this.state.authToken) return;
      if (this.isDevAuthMockUser()) {
        return;
      }
      try {
        const response = await ApiService.getMe();
        this.persistAuth({
          token: this.state.authToken,
          user: response.data.user,
          entitlement: response.data.entitlement,
        });
      } catch (error) {
        this.clearAuth();
        this.statusMessage = this.t("message.premium.sessionExpired");
        console.error("Auth refresh failed", error);
      }
    },
    async handleGoogleSuccess(idToken) {
      try {
        const response = await ApiService.loginWithGoogle(idToken);
        this.persistAuth(response.data);
        this.statusMessage = this.t("message.premium.loginSuccess");
        await this.fetchCourseCatalog();
        await this.loadStudyPlanCourses();
      } catch (error) {
        console.error("Google login failed", error);
        this.statusMessage = this.t("message.premium.loginFailed");
      }
    },
    handleGoogleError(error) {
      console.error("Google Sign-In error", error);
      this.statusMessage = this.t("message.premium.loginFailed");
    },
    async handleLogout() {
      try {
        if (this.state.authToken) {
          await ApiService.logout();
        }
      } catch (error) {
        void error;
      } finally {
        this.clearAuth();
        if (this.isDevStudyPlanMockEnabled()) {
          await this.fetchCourseCatalog();
          await this.loadStudyPlanCourses();
        } else {
          this.studyPlanCourses = [];
          this.allCourses = [];
        }
        this.statusMessage = this.t("message.premium.loggedOut");
      }
    },
    downloadJsonFile(payload, fileName) {
      const content = JSON.stringify(payload, null, 2);
      const blob = new Blob([content], {
        type: "application/json;charset=utf-8",
      });
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    },
    async downloadMyData() {
      if (!this.state.authToken) return;
      try {
        const response = await ApiService.getMyDataExport();
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
        this.downloadJsonFile(
          response.data,
          `onlygrades-account-export-${timestamp}.json`
        );
        this.statusMessage = this.t("message.premium.exportSuccess");
      } catch (error) {
        console.error("Failed exporting account data", error);
        this.statusMessage = this.t("message.premium.exportFailed");
      }
    },
    async deleteMyAccount() {
      if (!this.state.authToken) return;
      const confirmed = window.confirm(this.t("message.premium.deleteConfirm"));
      if (!confirmed) return;
      try {
        await ApiService.deleteMyAccount(true);
        this.clearAuth();
        this.studyPlanCourses = [];
        this.allCourses = [];
        this.statusMessage = this.t("message.premium.deleteSuccess");
      } catch (error) {
        console.error("Failed deleting account", error);
        this.statusMessage = this.t("message.premium.deleteFailed");
      }
    },
  },
};
</script>

<style scoped>
.account-container :deep(.v-card) {
  background: var(--og-surface) !important;
  border: 1px solid var(--og-border);
  border-radius: 18px !important;
  box-shadow: var(--og-shadow) !important;
}

.account-subtitle {
  color: var(--og-muted);
  margin-bottom: 16px;
}

.account-subtle-note {
  margin-top: -2px;
  margin-bottom: 8px;
  color: var(--og-muted);
  font-size: 0.86rem;
}

.dev-auth-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin: 10px 0 12px;
}

.dev-auth-chip {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px dashed var(--og-border);
  background: var(--og-surface);
  color: var(--og-muted);
  font-size: 0.8rem;
  white-space: nowrap;
}

.account-card,
.study-plan-card {
  margin-top: 20px;
  padding: 16px;
  border: 1px solid var(--og-border);
  border-radius: 14px;
  background: var(--og-surface-soft);
  text-align: left;
}

.section-title {
  margin-bottom: 12px;
  color: var(--og-text);
  font-family: "Newsreader", serif;
}

.button-row {
  margin-top: 10px;
}

.account-tools {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid var(--og-border);
}

.account-tools-title {
  margin: 0 0 8px;
  color: var(--og-muted);
  font-size: 0.88rem;
}

.study-plan-subtitle {
  margin-top: -4px;
  margin-bottom: 10px;
  color: var(--og-muted);
}

.study-plan-count {
  margin-top: 10px;
  margin-bottom: 8px;
  color: var(--og-muted);
  font-size: 0.9rem;
}

.study-plan-empty {
  margin: 0;
  color: var(--og-muted);
  font-size: 0.9rem;
}

.study-plan-list {
  list-style: none;
  padding: 0;
  margin: 0;
  border-top: 1px solid var(--og-border);
}

.study-plan-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 10px 0;
  border-bottom: 1px solid var(--og-border);
}

.study-plan-item:last-child {
  border-bottom: none;
}

.study-plan-item-text {
  color: var(--og-text);
  font-size: 0.92rem;
}

.study-plan-remove-btn {
  min-height: 34px;
  border: 1px solid var(--og-border);
  background: var(--og-surface);
  color: var(--og-muted);
  border-radius: 999px;
  padding: 0 10px;
  cursor: pointer;
  white-space: nowrap;
  font-size: 0.78rem;
}

.study-plan-remove-btn:hover {
  border-color: var(--og-primary);
  color: var(--og-primary-strong);
}

.study-plan-remove-btn:disabled {
  opacity: 0.7;
  cursor: default;
}

.status-message {
  margin-top: 16px;
  font-weight: 600;
  color: var(--og-primary-strong);
}
</style>
